import type { IPublicUser } from '@sravni/types/lib/auth';

import { BFF_API_ROUTES } from 'constants/apiRoutes';
import { ANY_AUTO_MODEL_INCOMING_ITEM } from 'constants/dictionaries';

import { baseRTKApi } from 'shared/api/baseApi';
import type { FormStepId } from 'shared/config/formStepId';
import { mapAutoManufactureYears, mapBrands, mapAutoModels } from 'shared/lib/mapDictionaries';
import { mapJobPositions } from 'shared/lib/mapDictionaries/mapJobPositions';
import type {
  TPassportUnitCodeSuggests,
  TLocationSettlement,
  TAutoModel,
  TAutoBrand,
  TAutoManufactureYear,
} from 'shared/types';
import type {
  TCustomerData,
  TCustomerRestoredWLData,
  TJobPositionSuggest,
  TLoansRestoredData,
} from 'shared/types/TCustomerService';

import { mapUnitCodesToSelectOptions } from 'entities/creditForm/lib/mapUnitCodesToSelectOptions';

import type { TCreditFormDictionaries } from '../types';

export const formQueries = baseRTKApi.injectEndpoints({
  endpoints: (build) => ({
    getAutoBrands: build.query<TCreditFormDictionaries['autoBrands'], void>({
      query: () => ({
        url: BFF_API_ROUTES.getAutoBrands,
      }),
      keepUnusedDataFor: 1000 * 60,
      transformResponse: (response: TAutoBrand[]) => mapBrands(response),
    }),
    getAutoModels: build.query<TCreditFormDictionaries['autoModels'], number | string>({
      query: (brandId) => ({
        url: BFF_API_ROUTES.getAutoModels,
        params: { brandId },
      }),
      keepUnusedDataFor: 10 * 60,
      transformResponse: (response: TAutoModel[], _, brandId) =>
        mapAutoModels({ brandId }, [ANY_AUTO_MODEL_INCOMING_ITEM, ...response]),
    }),
    getAutoManufactureYears: build.query<TCreditFormDictionaries['autoYears'], number | string>({
      query: (modelId) => ({
        url: BFF_API_ROUTES.getCarManufactureYears,
        params: { modelId },
      }),
      keepUnusedDataFor: 10 * 60,
      transformResponse: (response: TAutoManufactureYear[], _, modelId) =>
        mapAutoManufactureYears({ modelId }, response),
    }),
    getUnitCodes: build.query<ReturnType<typeof mapUnitCodesToSelectOptions>, string>({
      query: (query) => ({
        url: BFF_API_ROUTES.getUnitCodeSuggestions,
        params: { q: query },
      }),
      keepUnusedDataFor: 5 * 60,
      transformResponse: (response: TPassportUnitCodeSuggests, _) => mapUnitCodesToSelectOptions(response),
    }),
    getLocationSettlement: build.query<TLocationSettlement, string>({
      query: (address) => ({
        url: BFF_API_ROUTES.getLocationSettlement,
        params: { address },
      }),
      keepUnusedDataFor: 5 * 60,
      transformResponse: (response: TLocationSettlement, _) => response,
    }),
    getLocationByFiasId: build.query<ILocationV2, string>({
      query: (fiasId) => ({
        url: BFF_API_ROUTES.getLocationByFiasId,
        params: { fiasId },
      }),
      keepUnusedDataFor: 5 * 60,
      transformResponse: (response: ILocationV2, _) => response,
    }),
    getUserData: build.query<TCustomerData, void>({
      query: () => ({
        url: BFF_API_ROUTES.getUserData,
      }),
      extraOptions: { maxRetries: 1 },
    }),
    getUserDataAfterEsia: build.query<TCustomerData, FormStepId>({
      query: () => ({
        url: BFF_API_ROUTES.getUserData,
      }),
      extraOptions: { maxRetries: 1 },
    }),
    getAccountData: build.query<IPublicUser, void>({
      query: () => ({
        url: BFF_API_ROUTES.account,
      }),
      extraOptions: { maxRetries: 3 },
    }),
    restoreWhiteLabelUserData: build.query<TCustomerRestoredWLData, void>({
      query: () => ({
        url: BFF_API_ROUTES.restoreWhiteLabelUserData,
      }),
      extraOptions: { maxRetries: 1 },
    }),
    restoreUserData: build.query<TLoansRestoredData['customer'], string>({
      query: (restoreGuid) => ({
        url: BFF_API_ROUTES.restoreUserData,
        params: { restoreGuid },
      }),
      extraOptions: { maxRetries: 3 },
    }),
    getJobPositions: build.query<TCreditFormDictionaries['jobPositions'], string>({
      async queryFn(q, _queryApi, _extraOptions, fetchQuery) {
        if (q?.length < 3) {
          return {
            data: [],
          };
        }

        const result = await fetchQuery({
          url: BFF_API_ROUTES.getJobPositionSuggest,
          params: { q },
        });

        if (result.error)
          return {
            data: [],
          };

        return {
          data: mapJobPositions(result.data as TJobPositionSuggest[]),
        };
      },
      keepUnusedDataFor: 10 * 60,
      extraOptions: { maxRetries: 1 },
    }),
  }),
});

export const useGetAutoModels = formQueries.useLazyGetAutoModelsQuery;
export const useGetAutoManufactureYears = formQueries.useLazyGetAutoManufactureYearsQuery;
export const useGetUnitCodes = formQueries.useLazyGetUnitCodesQuery;
export const useGetLocationSettlement = formQueries.useLazyGetLocationSettlementQuery;
export const useGetLocationByFiasId = formQueries.useLazyGetLocationByFiasIdQuery;
export const useGetUserData = formQueries.useLazyGetUserDataQuery;
export const useRestoreUserData = formQueries.useLazyRestoreUserDataQuery;
export const useRestoreWhiteLabelUserData = formQueries.useLazyRestoreWhiteLabelUserDataQuery;
export const useGetJobPositions = formQueries.useLazyGetJobPositionsQuery;
